// The Fragen-Check workspace, at module level so it outlasts the view: the
// open list and its ticked questions, the grouping in flight, and the check
// being run. A check runs by calling `POST …/checks/{id}/next` until it is
// done — from here, not from a component, so it keeps running while the admin
// opens other views (as long as the browser window stays open).

import { signal } from "@preact/signals";

import { api } from "@/api/client";
import type {
  CheckResult,
  CheckStepResponse,
  QuestionCheck,
  QuestionGroup,
  QuestionList,
  QuestionListCreate,
} from "@/api/types";
import { t } from "@/i18n";
import { formatInt } from "@/lib/format";
import { MAX_QUESTIONS, withRows } from "@/lib/questionCheck";
import { errorDetail, httpStatus } from "@/lib/runtimeChange";

/** The waits before each retry after a network or server error; then the run pauses. */
export const RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000];
/** The wait after a step that finished nothing (other steps hold the claims).
 * It doubles with every further such step, up to MAX_IDLE_STEP_MS. */
export const IDLE_STEP_MS = 2_000;
export const MAX_IDLE_STEP_MS = 30_000;
/** No question finished for this long: the run pauses. A claim frees itself
 * after 10 minutes and a step takes at most about 6, so a healthy check
 * finishes one well within it. */
export const STALL_MS = 15 * 60_000;

export type RunPhase = "idle" | "running" | "pausing" | "paused" | "done";
export type StopReason =
  | "chat_paused"
  | "mode_disabled"
  | "storage_failed"
  | "network"
  | "not_found"
  | "failed"
  | "stalled";

export interface CheckRun {
  check: QuestionCheck | null;
  results: CheckResult[];
  /** The checked list's groups, which name the results' groups. */
  groups: QuestionGroup[];
  phase: RunPhase;
  /** Why the run stopped by itself; null while it runs or when the admin paused it. */
  reason: StopReason | null;
  /** The failure behind `failed`, in words. */
  error: string;
}

const IDLE: CheckRun = {
  check: null,
  results: [],
  groups: [],
  phase: "idle",
  reason: null,
  error: "",
};

export const checkRun = signal<CheckRun>(IDLE);
export const openList = signal<QuestionList | null>(null);
export const selectedRows = signal<ReadonlySet<number>>(new Set());
export const grouping = signal<{ busy: boolean; error: string }>({ busy: false, error: "" });
/** Moves whenever a stored list or check changes, so their tables reload. */
export const historyVersion = signal(0);

/** A step is in flight or about to be: the loop is alive. */
export const isRunning = (run: CheckRun): boolean =>
  run.phase === "running" || run.phase === "pausing";

/** The questions of a check that are through, failed ones included. */
const settledOf = (check: QuestionCheck): number => check.done + check.failed;

// Each loop owns a token. Starting, opening or closing a check takes a new
// one, and a loop whose token is gone ends without writing.
let runToken = 0;

// Ends the loop's current wait early, so a pause or a close takes effect at
// once instead of after the wait.
let wakeLoop: (() => void) | null = null;

/** Waits `ms`, or until a pause or a close wakes the loop. */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const finish = () => {
      window.clearTimeout(timer);
      if (wakeLoop === finish) wakeLoop = null;
      resolve();
    };
    const timer = window.setTimeout(finish, ms);
    wakeLoop = finish;
  });
}

const bumpHistory = () => {
  historyVersion.value = historyVersion.peek() + 1;
};

function update(patch: Partial<CheckRun>): void {
  checkRun.value = { ...checkRun.peek(), ...patch };
}

const errorText = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** A step's failure: a refusal the admin can act on, or trouble worth a retry.
 * `storage_failed` (503) is no trouble a retry mends: the step stored what it
 * could, and a retry would only ask new questions. */
function stopReason(err: unknown): StopReason {
  const status = httpStatus(err);
  if (status === null) return "network";
  const code = errorDetail(err).error;
  if (code === "chat_paused" || code === "mode_disabled" || code === "storage_failed") {
    return code;
  }
  if (status >= 500) return "network";
  return status === 404 ? "not_found" : "failed";
}

/** A refusal of the Fragen-Check API in words; the HTTP error when it names none. */
export function questionCheckError(err: unknown): string {
  const detail = errorDetail(err);
  switch (detail.error) {
    case "chat_paused":
      return t("qc.error.chat_paused");
    case "mode_disabled":
      return t("qc.error.mode_disabled");
    case "invalid_rows":
      return t("qc.error.invalid_rows", { max: formatInt(MAX_QUESTIONS) });
    case "invalid_page":
      return t("qc.error.invalid_page");
    case "not_found":
      return t("qc.error.not_found");
    case "question_list_invalid":
      return detail.reason === "too_many_rows"
        ? t("qc.error.too_many_rows", { max: formatInt(Number(detail.max_rows) || 0) })
        : t("qc.error.empty");
  }
  return httpStatus(err) === null ? t("qc.error.network") : errorText(err);
}

// --- The open list -------------------------------------------------------

/** Groups a pasted list; true once it is stored and open. */
export async function groupList(body: QuestionListCreate): Promise<boolean> {
  grouping.value = { busy: true, error: "" };
  try {
    const list = await api.createQuestionList(body);
    openList.value = list;
    selectedRows.value = new Set();
    grouping.value = { busy: false, error: "" };
    bumpHistory();
    return true;
  } catch (err) {
    grouping.value = { busy: false, error: questionCheckError(err) };
    return false;
  }
}

/** Opens a stored list with nothing ticked. */
export async function openSavedList(id: string): Promise<void> {
  const list = await api.questionList(id);
  openList.value = list;
  selectedRows.value = new Set();
}

export function closeList(): void {
  openList.value = null;
  selectedRows.value = new Set();
}

/** Ticks (on) or unticks the rows `ns` of the open list. */
export function tickRows(ns: readonly number[], on: boolean): void {
  selectedRows.value = withRows(selectedRows.peek(), ns, on);
}

export async function deleteSavedList(id: string): Promise<void> {
  await api.deleteQuestionList(id);
  if (openList.peek()?.id === id) closeList();
  bumpHistory();
}

// --- The check -------------------------------------------------------------

function mergeResults(
  earlier: readonly CheckResult[],
  finished: readonly CheckResult[],
): CheckResult[] {
  const byRow = new Map(earlier.map((result) => [result.n, result]));
  for (const result of finished) byRow.set(result.n, result);
  return [...byRow.values()];
}

function stop(reason: StopReason | null, error = ""): void {
  update({ phase: "paused", reason, error });
  bumpHistory();
}

/** The stored results, when this window holds fewer than the check finished:
 * a step whose answer never arrived, or one another window ran. */
async function completeResults(token: number): Promise<void> {
  const { check, results } = checkRun.peek();
  if (!check || results.length >= settledOf(check)) return;
  try {
    const stored = await api.questionCheck(check.id);
    if (token !== runToken) return;
    update({ results: mergeResults(checkRun.peek().results, stored.results) });
  } catch {
    // The results in hand stay; opening the check again loads them all.
  }
}

async function runLoop(token: number): Promise<void> {
  let failures = 0;
  let idleMs = IDLE_STEP_MS;
  const first = checkRun.peek().check;
  let settled = first ? settledOf(first) : 0;
  let progressAt = Date.now();
  for (;;) {
    const run = checkRun.peek();
    if (token !== runToken || !run.check) return;
    if (run.phase === "pausing") {
      stop(null);
      return;
    }
    let step: CheckStepResponse;
    try {
      step = await api.questionCheckNext(run.check.id);
    } catch (err) {
      if (token !== runToken) return;
      // The admin's pause wins; «Weiter» meets any lasting refusal again.
      if (checkRun.peek().phase === "pausing") {
        stop(null);
        return;
      }
      const reason = stopReason(err);
      if (reason === "network" && failures < RETRY_DELAYS_MS.length) {
        await wait(RETRY_DELAYS_MS[failures]);
        failures += 1;
        continue;
      }
      stop(reason, reason === "failed" ? errorText(err) : "");
      return;
    }
    if (token !== runToken) return;
    failures = 0;
    // Any window's finished question counts: another one may run the check too.
    if (settledOf(step.check) > settled) {
      settled = settledOf(step.check);
      progressAt = Date.now();
    }
    const done = step.check.status === "done";
    update({
      check: step.check,
      results: mergeResults(checkRun.peek().results, step.results),
      ...(done ? { phase: "done" as const } : {}),
    });
    if (done) {
      await completeResults(token);
      bumpHistory();
      return;
    }
    if (step.results.length > 0) {
      idleMs = IDLE_STEP_MS;
      continue;
    }
    // A pause asked for during the step ends the run now, before any wait.
    if (checkRun.peek().phase === "pausing") continue;
    if (Date.now() - progressAt >= STALL_MS) {
      stop("stalled");
      return;
    }
    await wait(idleMs);
    idleMs = Math.min(idleMs * 2, MAX_IDLE_STEP_MS);
  }
}

/** Starts a check of the rows `ns` of `list`, run from `page` ("" = none).
 * Throws what the start refused; the run itself goes on in the background. */
export async function startCheck(
  list: QuestionList,
  ns: readonly number[],
  page: string,
): Promise<void> {
  if (isRunning(checkRun.peek())) return;
  const check = await api.createQuestionCheck({
    list_id: list.id,
    rows: [...ns],
    ...(page ? { page } : {}),
  });
  const token = ++runToken;
  const done = check.status === "done";
  checkRun.value = { ...IDLE, check, groups: list.groups, phase: done ? "done" : "running" };
  bumpHistory();
  if (!done) void runLoop(token);
}

/** Stops the running check once the step in flight is back; a wait ends at once. */
export function pauseCheck(): void {
  if (checkRun.peek().phase !== "running") return;
  update({ phase: "pausing" });
  wakeLoop?.();
}

/** Continues a paused or stopped check, or takes back a pause not yet in effect. */
export function resumeCheck(): void {
  const run = checkRun.peek();
  if (run.phase === "pausing") {
    update({ phase: "running" });
    return;
  }
  if (!run.check || run.phase !== "paused" || run.reason === "not_found") return;
  update({ phase: "running", reason: null, error: "" });
  void runLoop(++runToken);
}

/** Puts the check away; a step still in flight is dropped, a wait ends at once. */
export function closeCheck(): void {
  runToken += 1;
  checkRun.value = IDLE;
  wakeLoop?.();
}

/** Opens a stored check with its results; an unfinished one waits for «Weiter». */
export async function showCheck(id: string): Promise<void> {
  if (isRunning(checkRun.peek())) return;
  const token = ++runToken;
  const detail = await api.questionCheck(id);
  // Without its list the results still show, their groups named by key.
  const groups = await api.questionList(detail.list_id).then(
    (list) => list.groups,
    () => [],
  );
  if (token !== runToken) return;
  const { results, ...check } = detail;
  checkRun.value = {
    ...IDLE,
    check,
    results,
    groups,
    phase: check.status === "done" ? "done" : "paused",
  };
}

export async function deleteSavedCheck(id: string): Promise<void> {
  const run = checkRun.peek();
  if (run.check?.id === id && isRunning(run)) return;
  await api.deleteQuestionCheck(id);
  if (checkRun.peek().check?.id === id) closeCheck();
  bumpHistory();
}
